import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { NavLink } from "@/components/NavLink";
import { CheckCircle, ArrowRight, ArrowDown, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLocation, Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface PricingSectionProps {
  variant?: "full" | "compact";
  showSchema?: boolean;
}

const pricingFaqItems = [
  {
    question: "Do you offer a free website build?",
    answer: "The website build is included as part of the monthly plan. We handle the design, setup, and launch so you can focus on your business."
  },
  {
    question: "How long does it take to launch?",
    answer: "Typically a few days to a couple weeks depending on how quickly we get your info, photos, and approvals."
  },
  {
    question: "Can I upgrade later?",
    answer: "Yes — you can upgrade from Core to Local SEO Expansion at any time."
  }
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://www.leafrushmarketing.com/#website-plans",
  "name": "Website Design Services for Lawn Care & Landscaping",
  "description": "Professional website design subscription services for lawn care and landscaping businesses. Choose from Core Website ($200/month), Local SEO Expansion ($500/month), or Custom solutions.",
  "provider": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Website Plans",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Core Website",
          "description": "A fast, modern website built to turn more visitors into calls and quote requests — and give your business a strong, trustworthy online presence."
        },
        "price": "200",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "200",
          "priceCurrency": "USD",
          "unitText": "month"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Expansion Website",
          "description": "Everything in the Core Website, plus a bigger local footprint: dedicated pages for each service and location you serve."
        },
        "price": "500",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "500",
          "priceCurrency": "USD",
          "unitText": "month"
        }
      }
    ]
  }
};

const pricingFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://www.leafrushmarketing.com/#pricing-faq",
  "mainEntity": pricingFaqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

// Comparison table data
const comparisonCategories = [
  {
    name: "Website Build",
    rows: [
      { feature: "Website included", core: true, expansion: true, custom: true },
      { feature: "Core pages (Home, About, Contact, Services)", core: true, expansion: true, custom: true },
      { feature: "Total pages", core: "7–14 pages", expansion: "~50–84 pages", custom: "Scoped" },
      { feature: "Mobile-first design", core: true, expansion: true, custom: true },
    ]
  },
  {
    name: "Speed + Conversion",
    rows: [
      { feature: "Speed optimization", core: true, expansion: true, custom: true },
      { feature: "Conversion-focused layout (CTAs, trust, structure)", core: true, expansion: true, custom: true },
    ]
  },
  {
    name: "SEO + Local Targeting",
    rows: [
      { feature: "Foundational on-page SEO", core: true, expansion: true, custom: true },
      { feature: "Local targeting built-in", core: true, expansion: true, custom: true },
      { feature: "Local SEO-focused site structure", core: "Foundational", expansion: true, custom: "Scoped" },
    ]
  },
  {
    name: "Content Expansion",
    rows: [
      { feature: "Service × Location pages", core: false, expansion: "Up to 80", custom: "Scoped" },
      { feature: "Services included", core: "—", expansion: "Up to 8", custom: "Scoped" },
      { feature: "Locations/service areas", core: "—", expansion: "Up to 10", custom: "Scoped" },
    ]
  },
  {
    name: "Maintenance + Updates",
    rows: [
      { feature: "Security hardening/monitoring", core: true, expansion: true, custom: true },
      { feature: "Backups (daily/weekly)", core: true, expansion: true, custom: true },
      { feature: "Plugin/theme/software updates", core: true, expansion: true, custom: true },
      { feature: "Small content updates (within reason)", core: true, expansion: true, custom: true },
      { feature: "Photo uploads", core: true, expansion: true, custom: true },
      { feature: "Review/testimonial uploads", core: true, expansion: true, custom: true },
    ]
  },
  {
    name: "Custom Features",
    rows: [
      { feature: "Advanced integrations (CRM/scheduling)", core: false, expansion: false, custom: true },
      { feature: "Unique design requirements", core: false, expansion: false, custom: true },
      { feature: "Special functionality", core: false, expansion: false, custom: true },
    ]
  },
];

const renderCellValue = (value: boolean | string) => {
  if (value === true) {
    return <CheckCircle className="h-5 w-5 text-primary mx-auto" />;
  }
  if (value === false) {
    return <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />;
  }
  return <span className="text-sm font-medium">{value}</span>;
};

const renderMobileCellValue = (value: boolean | string, planName: string) => {
  if (value === true) {
    return (
      <div className="flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-sm">{planName}</span>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground/60">
        <X className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm">{planName}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
      <span className="text-sm">{planName}: {value}</span>
    </div>
  );
};

export const PricingSection = ({ variant = "full", showSchema = false }: PricingSectionProps) => {
  const location = useLocation();
  const isOnWebsitesPage = location.pathname === "/websites";
  const comparisonLink = isOnWebsitesPage ? "#full-plan-comparison" : "/websites#full-plan-comparison";

  const handleComparisonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isOnWebsitesPage) {
      e.preventDefault();
      const element = document.getElementById("full-plan-comparison");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <>
      {showSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(serviceSchema)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(pricingFaqSchema)}
          </script>
        </Helmet>
      )}
      
      <section id="pricing" className="py-20 md:py-28">
        <div className="container px-4 md:px-6">
          {/* Section Header */}
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-5">Website Plans</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Choose the website plan that fits your goals. Every plan includes a professional site built to look trustworthy, load fast, and make it easy for customers to request a quote.
            </p>
          </div>

          {/* Overview Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Core Website */}
            <Card className="border-2 hover:border-primary transition-colors flex flex-col">
              <CardHeader className="pb-6 space-y-4">
                <Badge variant="secondary" className="w-fit text-sm px-3 py-1">Most Popular</Badge>
                <CardTitle className="text-2xl md:text-3xl">Core Website</CardTitle>
                <div>
                  <span className="text-5xl md:text-6xl font-bold text-primary">$200</span>
                  <span className="text-lg text-muted-foreground ml-1">/month</span>
                </div>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  A fast, modern website built to turn more visitors into calls and quote requests — and give your business a strong, trustworthy online presence.
                </p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col pt-0">
                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">7–14 page website</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Mobile-first + fast load times</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Conversion-optimized layout</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Foundational on-page SEO</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Ongoing maintenance + security</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Photo + review uploads included</span>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <a href="#get-started" aria-label="Get Started with Core Website">
                    <Button className="w-full text-base py-6" size="lg">Get Started</Button>
                  </a>
                  <Link 
                    to={comparisonLink}
                    onClick={handleComparisonClick}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                    aria-label="See full plan comparison for Core Website"
                  >
                    See Full Comparison <ArrowDown className="h-3 w-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Expansion Website */}
            <Card className="border-2 hover:border-primary transition-colors flex flex-col relative">
              <CardHeader className="pb-6 space-y-4">
                <Badge variant="outline" className="w-fit text-sm px-3 py-1 border-primary/50 text-primary">For Growth</Badge>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Local SEO Expansion</p>
                  <CardTitle className="text-2xl md:text-3xl">Expansion Website</CardTitle>
                </div>
                <div>
                  <span className="text-5xl md:text-6xl font-bold text-primary">$500</span>
                  <span className="text-lg text-muted-foreground ml-1">/month</span>
                </div>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  A full website plus dedicated service × location pages to strengthen visibility across your service area.
                </p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col pt-0">
                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Everything in Core Website</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Service + location page expansion</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Local SEO-focused structure + linking</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Up to 8 services and 10 locations</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Up to 80 service-area pages</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-base">Built in priority order (top locations first)</span>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <a href="#get-started" aria-label="Upgrade to Expansion Website">
                    <Button className="w-full text-base py-6" size="lg">Upgrade to Expansion</Button>
                  </a>
                  <Link 
                    to={comparisonLink}
                    onClick={handleComparisonClick}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                    aria-label="See full plan comparison for Expansion Website"
                  >
                    See Full Comparison <ArrowDown className="h-3 w-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Custom Website */}
            <Card className="border-2 hover:border-primary transition-colors flex flex-col bg-muted/20">
              <CardHeader className="pb-6 space-y-4">
                <div className="h-7" /> {/* Spacer for badge alignment */}
                <CardTitle className="text-2xl md:text-3xl">Custom Website</CardTitle>
                <div>
                  <span className="text-3xl md:text-4xl font-bold text-primary">Contact Us</span>
                </div>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  Need something fully custom? Advanced features, unique design, integrations, or special pages? Let's scope the right solution.
                </p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col pt-0">
                <div className="space-y-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-2">May include:</p>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-base text-muted-foreground">Advanced integrations (CRM, scheduling)</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-base text-muted-foreground">Unique design requirements</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-base text-muted-foreground">Multiple service lines or brands</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-base text-muted-foreground">Special pages or functionality</span>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <NavLink to="/contact" aria-label="Contact for Custom Website">
                    <Button variant="outline" className="w-full text-base py-6" size="lg">Contact Us</Button>
                  </NavLink>
                  <Link 
                    to={comparisonLink}
                    onClick={handleComparisonClick}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                    aria-label="See full plan comparison for Custom Website"
                  >
                    See Full Comparison <ArrowDown className="h-3 w-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Internal link */}
          <div className="text-center mt-8">
            <NavLink to="/about" className="text-base text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
              See how it works <ArrowRight className="h-4 w-4" />
            </NavLink>
          </div>

          {/* Full Plan Comparison Section */}
          {variant === "full" && (
            <div id="full-plan-comparison" className="mt-20 pt-8 scroll-mt-24">
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Full Plan Comparison</h3>
                <p className="text-base md:text-lg text-muted-foreground">
                  Compare features side-by-side to choose the plan that fits your goals.
                </p>
              </div>

              {/* Desktop Table */}
              <div className="hidden lg:block max-w-5xl mx-auto overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-4 px-4 font-semibold text-base w-2/5">Feature</th>
                      <th className="text-center py-4 px-4 font-semibold text-base w-1/5">
                        <div className="flex flex-col items-center">
                          <span>Core</span>
                          <span className="text-sm font-normal text-muted-foreground">$200/mo</span>
                        </div>
                      </th>
                      <th className="text-center py-4 px-4 font-semibold text-base w-1/5">
                        <div className="flex flex-col items-center">
                          <span>Expansion</span>
                          <span className="text-sm font-normal text-muted-foreground">$500/mo</span>
                        </div>
                      </th>
                      <th className="text-center py-4 px-4 font-semibold text-base w-1/5">
                        <div className="flex flex-col items-center">
                          <span>Custom</span>
                          <span className="text-sm font-normal text-muted-foreground">Contact</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonCategories.map((category, catIndex) => (
                      <>
                        <tr key={`cat-${catIndex}`} className="bg-muted/30">
                          <td colSpan={4} className="py-3 px-4 font-bold text-base">
                            {category.name}
                          </td>
                        </tr>
                        {category.rows.map((row, rowIndex) => (
                          <tr key={`row-${catIndex}-${rowIndex}`} className="border-b border-border/50 hover:bg-muted/10">
                            <td className="py-3 px-4 text-base">{row.feature}</td>
                            <td className="py-3 px-4 text-center">{renderCellValue(row.core)}</td>
                            <td className="py-3 px-4 text-center">{renderCellValue(row.expansion)}</td>
                            <td className="py-3 px-4 text-center">{renderCellValue(row.custom)}</td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Accordion */}
              <div className="lg:hidden max-w-2xl mx-auto">
                <Accordion type="multiple" className="w-full space-y-2">
                  {comparisonCategories.map((category, catIndex) => (
                    <AccordionItem 
                      key={`mobile-cat-${catIndex}`} 
                      value={`category-${catIndex}`}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="text-base font-bold hover:no-underline py-4">
                        {category.name}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="space-y-4">
                          {category.rows.map((row, rowIndex) => (
                            <div key={`mobile-row-${catIndex}-${rowIndex}`} className="border-b border-border/30 pb-3 last:border-0 last:pb-0">
                              <p className="text-base font-medium mb-2">{row.feature}</p>
                              <div className="space-y-1 pl-2">
                                {renderMobileCellValue(row.core, "Core")}
                                {renderMobileCellValue(row.expansion, "Expansion")}
                                {renderMobileCellValue(row.custom, "Custom")}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          )}

          {/* Pricing FAQ */}
          {variant === "full" && (
            <div className="max-w-2xl mx-auto mt-16">
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-center">Pricing Questions</h3>
              <Accordion type="single" collapsible className="w-full">
                {pricingFaqItems.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-base py-4">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PricingSection;
